import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@meno/ui/components/ui/dialog'
import { Label } from '@meno/ui/components/ui/label'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import { Textarea } from '@meno/ui/components/ui/textarea'
import { companionsFromEntity, type Companions, type CompanionFields } from '@/components/companionsModel'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import CompanionSection from '@/components/CompanionSection'
import { NavItem, NavRail } from '@/components/DialogNavRail'

// The dialog opens straight into content selection (an embedded
// PlaylistContentPicker, not a nested "Add content" modal) with a left nav
// rail to switch over to Companions (audio / transition). Stop screens have
// no companions, so they skip the rail entirely and just get the picker.
type NavKey = 'content' | 'companions'

// Shared types — kept here so MediaPage / Content Hub / playlist details
// all agree on the playlist shape used by this dialog.
export type PlaylistMediaFile = {
  id: number
  original_name: string
  file_type: string
  url: string
  duration?: number
}
export type PlaylistLinkItem = {
  id: number
  name: string
  interactive_link: string
}
export type PlaylistInput = {
  id: number
  name: string
  description?: string
  media: PlaylistMediaFile[]
  media_durations: Record<number, number>
  links?: PlaylistLinkItem[]
  link_durations?: Record<number, number>
  media_order?: Record<number, number>
  link_order?:  Record<number, number>
  is_event?: boolean
  event_start_date?: string | null
  event_end_date?: string | null
  event_start_time?: string | null
  event_end_time?: string | null
} & CompanionFields
export type EventFields = {
  is_event: boolean
  event_start_date: string | null
  event_end_date: string | null
  event_start_time: string | null
  event_end_time: string | null
}

export type PlaylistDialogProps = {
  playlist: PlaylistInput | null
  allMedia: PlaylistMediaFile[]
  allLinks?: PlaylistLinkItem[]
  thumbCache: Record<number, string>
  // 'stop_screen' reuses this exact form for stop screen creation (same
  // name + media-with-durations shape) — it retitles the dialog and hides
  // the playlist-only sections (description, links, companions). The onSave
  // signature is unchanged; stop-screen callers just ignore the extras.
  variant?: 'playlist' | 'stop_screen'
  onSave: (
    name: string,
    description: string,
    mediaIds: number[],
    mediaDurations: Record<number, number>,
    linkIds: number[],
    linkDurations: Record<number, number>,
    mediaOrder: Record<number, number>,
    linkOrder: Record<number, number>,
    event: EventFields,
    companions: Companions,
  ) => Promise<void>
  onClose: () => void
}

const fmtDur = (sec: number) => `${Math.floor(sec / 60)}:${Math.floor(sec % 60).toString().padStart(2, '0')}`

// Narrowed slices of the picker's PickedItem union — the working selection
// only ever holds these two kinds (the picker is never opened with a
// 'playlists' tab here, and audioOnly is never set).
type PickedMedia = Extract<PickedItem, { kind: 'media' }>
type PickedLink   = Extract<PickedItem, { kind: 'link' }>

// PlaylistDialog — create / edit a playlist. Lifted out of MediaPage so
// Content Hub and any future caller can reuse it. The component is
// self-contained; the caller supplies the asset catalogues + the onSave
// callback that hits the backend.
export default function PlaylistDialog({
  playlist, variant = 'playlist', onSave, onClose,
}: PlaylistDialogProps) {
  const { t } = useTranslation()
  const isStopScreen = variant === 'stop_screen'
  const [name, setName]             = useState(playlist?.name ?? '')
  const [description, setDescription] = useState(playlist?.description ?? '')
  // Publish companions — audio / transition co-published with the playlist.
  // Seeded from the row on edit so picks survive round-trips.
  const [companions, setCompanions] = useState<Companions>(companionsFromEntity(playlist))
  const [saving, setSaving]         = useState(false)
  const [activeNav, setActiveNav]   = useState<NavKey>('content')
  // The working media/link selection, mirrored live from the embedded
  // picker's onSelectionChange — this is the single source of truth for
  // both the running total and the final save payload.
  const [pickedMedia, setPickedMedia] = useState<PickedMedia[]>([])
  const [pickedLinks, setPickedLinks] = useState<PickedLink[]>([])

  const hasCompanions = companions.audioId != null || companions.audioPlaylistId != null || companions.transition != null
  const itemCount = pickedMedia.length + pickedLinks.length

  const handleSelectionChange = (items: PickedItem[]) => {
    setPickedMedia(items.filter((i): i is PickedMedia => i.kind === 'media'))
    setPickedLinks(items.filter((i): i is PickedLink => i.kind === 'link'))
  }

  const computeTotal = () => {
    let total = 0
    pickedMedia.forEach(m => { total += m.duration ?? 10 })
    pickedLinks.forEach(l => { total += l.duration ?? 10 })
    return total
  }

  const handleSave = async () => {
    if (!name.trim() || saving) return
    const mediaIds = pickedMedia.map(m => m.id)
    const mediaDurations: Record<number, number> = {}
    pickedMedia.forEach(m => { mediaDurations[m.id] = m.duration ?? 10 })
    const linkIds = pickedLinks.map(l => l.id)
    const linkDurations: Record<number, number> = {}
    pickedLinks.forEach(l => { linkDurations[l.id] = l.duration ?? 10 })
    // Positions interleave: media first, then links. Caller can reorder on
    // the playlist detail page after creation.
    const mediaOrder: Record<number, number> = {}
    mediaIds.forEach((id, i) => { mediaOrder[id] = i })
    const linkOrder: Record<number, number> = {}
    linkIds.forEach((id, i) => { linkOrder[id] = mediaIds.length + i })
    // Event mode was removed from playlist creation; always send the
    // not-an-event shape so the onSave / createPlaylist signature is stable.
    const event: EventFields = {
      is_event: false, event_start_date: null, event_end_date: null,
      event_start_time: null, event_end_time: null,
    }
    setSaving(true)
    try {
      await onSave(
        name.trim(), description.trim(),
        mediaIds, mediaDurations,
        linkIds, linkDurations,
        mediaOrder, linkOrder,
        event,
        companions,
      )
    } finally { setSaving(false) }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="max-w-5xl p-0 gap-0 overflow-hidden flex flex-col max-h-[92vh]">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <DialogTitle>
            {isStopScreen
              ? t('stopScreen.newTitle', 'New Stop Screen')
              : playlist ? t('common.editPlaylist', 'Edit Playlist') : t('common.newPlaylist', 'New Playlist')}
          </DialogTitle>
        </DialogHeader>

        <div className={`px-6 py-4 border-b border-border shrink-0 grid gap-3 ${isStopScreen ? '' : 'sm:grid-cols-2'}`}>
          <TextInput
            label={isStopScreen ? t('stopScreen.name', 'Name') : t('common.playlistName', 'Playlist name')}
            value={name}
            onChange={setName}
            required
            maxLength={150}
            lang="any"
            placeholder={isStopScreen
              ? t('stopScreen.namePlaceholder', 'e.g. Idle attract loop')
              : t('common.playlistPlaceholder', 'e.g. Morning Promo')}
            autoFocus
          />
          {/* Stop screens have no description column — hide the field. */}
          {!isStopScreen && (
            <div className="grid gap-1.5">
              <Label>{t('common.description', 'Description')}</Label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                placeholder={t('common.optionalDescription', 'Optional description...')}
                className="resize-none"
              />
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex">
          {/* Left nav rail — Content is the direct/default destination;
              Companions is a tab away instead of buried in a top strip.
              Stop screens only ever deal with content, so they skip the
              rail and just get the picker full-width. */}
          {!isStopScreen && (
            <NavRail>
              <NavItem
                active={activeNav === 'content'}
                icon={ImageIcon}
                label={t('playlist.navContent', 'Content')}
                count={itemCount}
                onClick={() => setActiveNav('content')}
              />
              <NavItem
                active={activeNav === 'companions'}
                icon={Sparkles}
                label={t('playlist.navCompanions', 'Companions')}
                dot={hasCompanions}
                onClick={() => setActiveNav('companions')}
              />
            </NavRail>
          )}

          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Both panels stay mounted (hidden via CSS) so the embedded
                picker's internal state survives switching to Companions
                and back. */}
            <div className={`flex-1 min-h-0 flex flex-col ${activeNav === 'content' || isStopScreen ? '' : 'hidden'}`}>
              <PlaylistContentPicker
                open
                mode="embedded"
                excludeIds={playlist?.media.map(m => m.id) ?? []}
                excludeLinkIds={playlist?.links?.map(l => l.id) ?? []}
                tabs={isStopScreen ? ['all', 'media', 'documents'] : ['all', 'media', 'documents', 'links']}
                onSelectionChange={handleSelectionChange}
                onClose={() => {}}
                onConfirm={() => {}}
              />
            </div>
            {!isStopScreen && (
              <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 ${activeNav === 'companions' ? '' : 'hidden'}`}>
                <CompanionSection value={companions} onChange={setCompanions} />
              </div>
            )}
          </div>
        </div>

        {itemCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-6 py-2 border-t border-border shrink-0">
            <Clock className="w-4 h-4" />
            <span>{t('common.totalRuntime', 'Total runtime')}: <strong className="text-foreground">{fmtDur(computeTotal())}</strong></span>
          </div>
        )}

        <DialogFooter className="gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" disabled={saving} onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button disabled={!name.trim() || saving} onClick={handleSave} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isStopScreen
              ? t('stopScreen.createButton', 'Create Stop Screen')
              : playlist ? t('common.save', 'Save') : t('playlist.createButton', 'Create Playlist')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
